import { animate, motion, useMotionValue, useTransform } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { useEffect } from 'react'

import { fmtNumar } from '@/lib/format'
import { cn } from '@/lib/utils'

/** MotionValue-driven count-up that renders through `format`. */
function AnimatedNumber({
  value,
  format,
  duration = 1.1,
}: {
  value: number
  format: (n: number) => string
  duration?: number
}) {
  const mv = useMotionValue(0)
  const text = useTransform(mv, (v) => format(v))
  useEffect(() => {
    const controls = animate(mv, value, { duration, ease: [0.16, 1, 0.3, 1] })
    return () => controls.stop()
  }, [mv, value, duration])
  return <motion.span>{text}</motion.span>
}

export interface KpiCardProps {
  label: string
  value: number
  /** number formatter (default: RO integer). Pass `fmtRON`, `fmtKg`, … */
  format?: (n: number) => string
  icon?: LucideIcon
  /** small caption under the number, e.g. "+12% vs. ieri" */
  delta?: string
  deltaTone?: 'pine' | 'tomato' | 'muted'
  /** faint helper line */
  hint?: string
  /** stagger index for the reveal animation */
  index?: number
  className?: string
}

const deltaClass: Record<NonNullable<KpiCardProps['deltaTone']>, string> = {
  pine: 'text-pine',
  tomato: 'text-tomato',
  muted: 'text-muted-foreground',
}

export function KpiCard({
  label,
  value,
  format = fmtNumar,
  icon: Icon,
  delta,
  deltaTone = 'muted',
  hint,
  index = 0,
  className,
}: KpiCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'group relative overflow-hidden rounded-xl border border-border bg-card p-5 shadow-paper amber-wash',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {Icon && (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-ink">
            <Icon className="size-4" />
          </span>
        )}
      </div>

      <div className="mt-3 font-mono text-3xl font-semibold tracking-tight text-amber-ink nums">
        <AnimatedNumber value={value} format={format} />
      </div>

      {(delta || hint) && (
        <div className="mt-1.5 flex items-baseline gap-2 text-xs">
          {delta && <span className={cn('font-medium', deltaClass[deltaTone])}>{delta}</span>}
          {hint && <span className="text-muted-foreground">{hint}</span>}
        </div>
      )}
    </motion.div>
  )
}
